import {useDisclosure} from '@mantine/hooks';
import {ConflictError, InvalidTokenError} from '@packages/utils';
import {useQueryClient} from '@tanstack/react-query';
import {parseCookies} from 'nookies';
import {useCallback, useEffect, useState} from 'react';
import {isReleasedEntryManagement} from '../../../config/unReleasePage';
import {
  deleteCalendar,
  registCalendar,
} from '../../../core/repositories/calendarRepository';
import {useCreateStep} from '../../../core/repositories/mypageEntryManagementsRepository';
import {useIsSessionExpired} from '../../../core/usecases/auth/useIsSessionExpired';
import {ENTRY_SOURCE_TYPE_SCHEDULE_REGISTRATION} from '../../entryManagements/constants/entrySourceTypes';

type RecruitmentData = {
  companyId: number;
  companyName: string;
  entryTitle: string;
  detailURL: string;
};

type Props = {
  scheduleId: number;
  isRegisteredInitial: boolean;
  registeredCountInitial: number;
  recruitmentData?: RecruitmentData;
};

export const useHandleCalendar = ({
  scheduleId,
  isRegisteredInitial,
  registeredCountInitial,
  recruitmentData,
}: Props) => {
  const [registeredCount, setRegisteredCount] = useState<number>(
    registeredCountInitial,
  );
  const [isRegistered, setRegistered] = useState<boolean>(isRegisteredInitial);
  const [
    notificationOpened,
    {open: notificationOpen, close: notificationClose},
  ] = useDisclosure(false);
  const {updateIsSessionExpired} = useIsSessionExpired();
  const createStep = useCreateStep();
  const queryClient = useQueryClient();

  useEffect(() => {
    setRegisteredCount(registeredCountInitial);
    setRegistered(isRegisteredInitial);
  }, [scheduleId, isRegisteredInitial, registeredCountInitial]);

  // 通知は2秒後に消える
  useEffect(() => {
    if (notificationOpened) {
      const timer = setTimeout(() => {
        notificationClose();
      }, 2000);

      return () => {
        clearTimeout(timer);
      };
    }
  }, [notificationOpened]);

  const registCalendarUsecase = async () => {
    const cookies = parseCookies();
    const result = await registCalendar({
      cookies,
      scheduleId,
    });
    if (!result.ok && result.error === InvalidTokenError) {
      updateIsSessionExpired(true);
    }

    // サーバー上で既に登録済み（409）の場合はローカル state を同期して終了
    if (!result.ok && result.error === ConflictError) {
      setRegistered(true);
      queryClient.invalidateQueries({queryKey: ['RecruitmentList']});
      return;
    }

    if (!result.ok) {
      return;
    }

    setRegistered(true);
    notificationOpen();
    setRegisteredCount(registeredCount + 1);
    queryClient.invalidateQueries({queryKey: ['RecruitmentList']});

    // entry-management作成（カレンダー登録成功後）
    // hasCompletedEntryを送信しない:
    // - 既に選考管理がある場合: 何も変更されない
    // - 選考管理がない場合: 未エントリー状態で作成される
    if (recruitmentData && isReleasedEntryManagement()) {
      try {
        await createStep.mutateAsync({
          companyID: recruitmentData.companyId,
          companyName: recruitmentData.companyName,
          stepName: '',
          stepOrder: 0,
          entryTitle: recruitmentData.entryTitle,
          entrySourceUrl: recruitmentData.detailURL,
          entrySourceType: ENTRY_SOURCE_TYPE_SCHEDULE_REGISTRATION,
          recruitmentItemScheduleID: scheduleId,
          notes: `予定詳細\n${recruitmentData.detailURL}`,
          resultStatus: '',
        });
      } catch (e) {
        // 選考管理が既に存在する（409）場合はカレンダー登録自体は成功扱いのまま無視
        if ((e as Error)?.message === ConflictError) {
          return;
        }
        throw e;
      }
    }
  };

  const deleteCalendarUsecase = async () => {
    const cookies = parseCookies();
    const result = await deleteCalendar({
      cookies,
      scheduleId,
    });
    if (!result.ok && result.error === InvalidTokenError) {
      updateIsSessionExpired(true);
    }

    if (!result.ok) {
      return;
    }

    setRegistered(false);
    setRegisteredCount(registeredCount - 1);
    queryClient.invalidateQueries({queryKey: ['RecruitmentList']});
  };

  const handleCalendar = useCallback(async () => {
    if (isRegistered) {
      return deleteCalendarUsecase();
    }

    return registCalendarUsecase();
  }, [isRegistered]);

  return {
    isRegistered,
    registeredCount,
    handleCalendar,
    notificationOpened,
  };
};
